// ===== Packages =====

import React, {
    useRef,
    useState,
    useEffect,
    useMemo,
    useCallback,
}                                               from 'react';
import { createPortal }                         from 'react-dom';
import styled                                   from 'styled-components';
import { ReactSVG }                             from 'react-svg';
import {
    Descendant,
}                                               from 'slate';
import {
    HttpsCallableResult,
}                                               from 'firebase/functions';
import { v4 as uuidv4 }                         from 'uuid';

// ===== Services =====

import {
    setColorLightness,
    applyCharacterLimit,
    fetchURLMetadata,
    getPost,
    getLinkPreviewSecondaryText,
    recordUserAction,
    isMobile,
    getPressEventHandlers,
    getTouchMouseEventHandlers,
    updateMedia,
    isTestMode,
    copyToClipboard,
    buildInternalPostLinkClipboardURL,
    resolveInlineLinkCardPlacement,
    resolveInternalPostLinkPreviewData,
    resolveInternalPostLinkTarget,
}                                               from '../../../services';

// ===== Hooks =====

import { useTimeout }                           from '../../../hooks';

// ===== Components =====

import { Transition }                           from '../../Transition';
import {
    LinkCardImage,
    LinkCardTitle,
    LinkCardAddress,
    TemporaryLinkText,
    LinkCardContainer,
    LinkCardTopContent,
    LinkCardInfoContainer,
    LinkCardImageContainer,
}                                               from '../helpers/PortableToolbar';
import Button                                   from '../helpers/Button';

// ===== Styles =====

import {
    SlateLeaf,
}                                               from '../../../styles';

// ===== Interfaces =====

import {
    IGetPostResult,
    ICoord,
    IInlineLinkPreviewCardOpenedEventDetail,
    IInternalPostLinkPreviewData,
    IInternalPostLinkTarget,
    ILinkMetadata,
    IReadPostChapterItem,
    ISnackbarItem,
}                                               from '../../../interfaces';

// ===== Enums =====

import {
    MEDIA_TYPE,
    CURSOR_TARGET,
    EDITOR_TOOLBAR_TYPE,
    INTERACTABLE_OBJECT,
    USER_ACTION_TYPE,
    EDITOR_CONTEXT_TYPE,
    POST_RESPONSE_TIER,
    BUTTON_TYPE,
}                                               from '../../../enums';

import FONT_TYPE                                from '../../../constants/fontType';
import {
    INTERNAL_POST_LINK_CONSOLE_LOG_STORAGE_KEY,
    shouldEnableInternalPostLinkConsoleLogs,
}                                               from '../../../constants/debugFlags';

// ===== Images =====

import LinkIcon                                 from '../../../images/editor/link.svg';
import SpinnerIcon                              from '../../../images/editor/spinner.svg';
import CautionIcon                              from '../../../images/caution.svg';

// ===== Constants =====

import CURSOR_SIGN                              from '../../../constants/cursorSigns';
import {
    BODY_FONT_SIZE,
    FADE_IN_DEFAULT_STYLE,
    FADE_IN_TRANSITION_STYLES,
    HOVER_TARGET_CLASSNAME,
    LINK_CARD_FONT_MULTIPLIER,
    DEFAULT_SNACKBAR_VISIBLE_DURATION,
    SUCCESS_BACKGROUND_LIGHTNESS_VALUE,
    ERROR_BACKGROUND_LIGHTNESS_VALUE,
}                                               from '../../../constants/generalConstants';
import {
    LINK_CARD_IMAGE_WIDTH,
    LINK_CARD_IMAGE_HEIGHT,
    LINK_CARD_TRANSITION_DURATION,
    LINK_CARD_MAX_TITLE_COUNT,
    LINK_CARD_WITH_METADATA_HEIGHT,
    LINK_CARD_WITHOUT_METADATA_HEIGHT,
    LINK_CARD_WITH_METADATA_WIDTH,
    LINK_CARD_WITHOUT_METADATA_WIDTH,
}                                               from '../helpers/constants';

// ===== Styles =====

import { theme as themeObj }                    from '../../../themes/theme-context';

const TRANSITION_LINK_CARD_DELAY = 200;
const INLINE_LINK_PREVIEW_CARD_OPENED_EVENT = 'inline-link-preview-card-opened';
const SNACKBAR_MESSAGE_COPY_LINK_SUCCESS = 'Copied link to clipboard!';
const SNACKBAR_MESSAGE_COPY_LINK_ERROR = 'There was a problem copying link to your clipboard';
const hydratedInternalPostLinkPreviewDataCache = new Map<string, IInternalPostLinkPreviewData>();

/**
 * Reader mode only hydrates the active scope on the critical path, so internal
 * links that point at another chapter need a small merge step before the shared
 * preview resolver can recover the destination snippet.
 */
function buildHydratedInternalPostLinkPreviewChapters(data: {
    chapters?: IReadPostChapterItem[],
    target: IInternalPostLinkTarget,
    postResult: IGetPostResult,
}): IReadPostChapterItem[] | undefined {
    const {
        chapters,
        target,
        postResult,
    } = data;
    const baseChapters = [...(chapters || postResult.post?.chapters || [])];
    if (!target.chapterId || !postResult.chapter) {
        return baseChapters.length > 0 ? baseChapters : undefined;
    }

    const hydratedChapter: IReadPostChapterItem = {
        ...postResult.chapter,
        ...(target.sectionId && postResult.chapterSection
            ? {
                sections: (() => {
                    const baseSections = [...(postResult.chapter.sections || [])];
                    const targetSectionIndex = baseSections.findIndex((section) => (
                        section.id === target.sectionId
                    ));
                    if (targetSectionIndex >= 0) {
                        baseSections[targetSectionIndex] = {
                            ...baseSections[targetSectionIndex],
                            ...postResult.chapterSection,
                        };
                        return baseSections;
                    }

                    return [
                        ...baseSections,
                        postResult.chapterSection,
                    ];
                })(),
            }
            : {}
        ),
    };
    const hydratedChapterIndex = baseChapters.findIndex((chapter) => chapter.id === hydratedChapter.id);
    if (hydratedChapterIndex >= 0) {
        baseChapters[hydratedChapterIndex] = {
            ...baseChapters[hydratedChapterIndex],
            ...hydratedChapter,
            sections: hydratedChapter.sections || baseChapters[hydratedChapterIndex].sections,
        };
        return baseChapters;
    }

    return [
        ...baseChapters,
        hydratedChapter,
    ];
}

function buildInternalPostLinkPreviewCacheKey(data: {
    postId?: string,
    href: string,
}): string | null {
    const {
        postId,
        href,
    } = data;
    const normalizedPostId = `${postId || ''}`.trim();
    const normalizedHref = `${href || ''}`.trim();
    if (!normalizedPostId || !normalizedHref) {
        return null;
    }

    return `${normalizedPostId}::${normalizedHref}`;
}

/**
 * Announces that one inline link card became active so sibling links can drop
 * any delayed hide and disappear before the next card animates in.
 */
function dispatchInlineLinkPreviewCardOpened(previewId: string): void {
    if (typeof window === 'undefined') {
        return;
    }

    window.dispatchEvent(new CustomEvent<IInlineLinkPreviewCardOpenedEventDetail>(
        INLINE_LINK_PREVIEW_CARD_OPENED_EVENT,
        {
            detail: {
                previewId,
            },
        },
    ));
}

/**
 * Finds the next hover target when a mouse leave event provides one. Touch
 * events use the same handler through shared helpers, but they do not expose a
 * related target.
 */
function getInlineLinkPreviewMouseLeaveRelatedTarget(
    e: React.MouseEvent | React.TouchEvent,
): EventTarget | null {
    if (!('relatedTarget' in e)) {
        return null;
    }

    return e.relatedTarget;
}

InlineLink.defaultProps = {
    readOnly: false,
    hasSuccess: false,
    hasError: false,
    rootPostTitle: undefined,
    rootPostValueNodes: undefined,
    rootPostPreviewValueNodes: undefined,
    chapters: undefined,
    onNavigateInternalPostLink: undefined,
    internalPostLinkNavigationActive: false,
    setSnackbarData: undefined,
};
interface Props {
    customCursorActive: boolean,
    id: string,
    href: string,
    color: string,
    readOnly?: boolean,
    currentSessionId: string | null,
    postId: string | undefined,
    editorType: EDITOR_CONTEXT_TYPE,
    rootPostTitle?: string,
    rootPostValueNodes?: Descendant[],
    rootPostPreviewValueNodes?: Descendant[],
    chapters?: IReadPostChapterItem[],
    parentRef: HTMLElement | null,
    hasSuccess?: boolean,
    hasError?: boolean,
    onNavigateInternalPostLink?: (target: IInternalPostLinkTarget) => void,
    internalPostLinkNavigationActive?: boolean,
    setSnackbarData?: React.Dispatch<React.SetStateAction<ISnackbarItem>>,
    onCursorEnter: (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ) => void,
    onCursorLeave: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
    children: React.ReactElement,
    attributes: any,
}
function InlineLink({
    customCursorActive,
    id,
    href,
    color,
    children,
    readOnly = false,
    currentSessionId,
    postId,
    editorType,
    rootPostTitle,
    rootPostValueNodes,
    rootPostPreviewValueNodes,
    chapters,
    hasSuccess,
    hasError,
    onNavigateInternalPostLink,
    internalPostLinkNavigationActive = false,
    setSnackbarData,
    onCursorEnter,
    onCursorLeave,
    parentRef,
    attributes,
}: Props): JSX.Element {
    const shouldEmitInternalPostLinkConsoleLogs = useMemo(() => {
        if (process.env.NODE_ENV === 'production') {
            return false;
        }

        if (typeof window === 'undefined') {
            return shouldEnableInternalPostLinkConsoleLogs({
                envValue: process.env.REACT_APP_ENABLE_INTERNAL_POST_LINK_LOGS,
            });
        }

        let storedValue: string | null = null;
        try {
            storedValue = window.localStorage.getItem(INTERNAL_POST_LINK_CONSOLE_LOG_STORAGE_KEY);
        } catch (_error) {
            storedValue = null;
        }

        return shouldEnableInternalPostLinkConsoleLogs({
            search: window.location.search,
            storedValue,
            envValue: process.env.REACT_APP_ENABLE_INTERNAL_POST_LINK_LOGS,
        });
    }, []);
    const logInternalPostLink = useCallback((
        event: string,
        payload?: Record<string, unknown>,
    ): void => {
        if (!shouldEmitInternalPostLinkConsoleLogs) return;

        // eslint-disable-next-line no-console
        console.info('[internal-post-link]', event, payload || {});
    }, [shouldEmitInternalPostLinkConsoleLogs]);

    // ===== Refs =====

    // We use this ref to access InlineLink ref because
    // there is an issue assigning ref directly using figure
    //
    // This likely has to do with Slate attributes
    const childRef = useRef<HTMLSpanElement | null>(null);
    const linkCardRef = useRef<HTMLDivElement | null>(null);
    const inlineLinkPreviewCardIdRef = useRef<string>(uuidv4());
    const linkCardAnchorPointRef = useRef<ICoord | undefined>(undefined);

    // ===== State =====

    const [showCard, setShowCard] = useState<boolean>(false);
    const [cardAbove, setCardAbove] = useState<boolean>(true);
    // stores OG Metadata of cartridge with website
    const [linkMetadata, setLinkMetadata] = useState<ILinkMetadata | null>(null);
    const [hydratedInternalPostLinkPreviewData, setHydratedInternalPostLinkPreviewData] = useState<IInternalPostLinkPreviewData | null>(null);
    const [linkCardTop, setLinkCardTop] = useState<number>(0);
    const [linkCardLeft, setLinkCardLeft] = useState<number>(0);
    const internalPostLinkTarget = useMemo<IInternalPostLinkTarget | null>(() => (
        resolveInternalPostLinkTarget(href)
    ), [href]);
    const internalPostLinkPreviewCacheKey = useMemo(() => (
        buildInternalPostLinkPreviewCacheKey({
            postId,
            href,
        })
    ), [
        href,
        postId,
    ]);
    const internalPostLinkPreviewData = useMemo<IInternalPostLinkPreviewData | null>(() => (
        resolveInternalPostLinkPreviewData({
            href,
            rootPostTitle,
            rootPostValueNodes,
            rootPostPreviewValueNodes,
            chapters,
        })
    ), [
        href,
        rootPostTitle,
        rootPostValueNodes,
        rootPostPreviewValueNodes,
        chapters,
    ]);
    const resolvedInternalPostLinkPreviewData = hydratedInternalPostLinkPreviewData || internalPostLinkPreviewData;
    const hasDetailedLinkCard = !!(linkMetadata || resolvedInternalPostLinkPreviewData);
    const internalPreviewScopeText = [
        resolvedInternalPostLinkPreviewData?.chapterTitle,
        resolvedInternalPostLinkPreviewData?.sectionTitle,
    ]
        .filter((value) => !!value)
        .join(' / ');
    const internalPreviewPrimaryText = internalPreviewScopeText;
    const internalPreviewSecondaryText = resolvedInternalPostLinkPreviewData?.snippet || '';
    const internalPreviewUsesFallbackSnippet = !!resolvedInternalPostLinkPreviewData?.usesFallbackSnippet;
    const internalPreviewUsesAuthLockedFallbackSnippet = !!resolvedInternalPostLinkPreviewData?.usesAuthLockedFallbackSnippet;
    const secondaryText = getLinkPreviewSecondaryText(linkMetadata);

    // ===== Methods =====

    const onCopyInternalPostLink = (
        e: React.MouseEvent | React.TouchEvent | React.PointerEvent,
    ): void => {
        e.preventDefault();
        e.stopPropagation();

        const clipboardURL = buildInternalPostLinkClipboardURL({
            href,
            origin: window.location.origin,
        });

        copyToClipboard(
            clipboardURL,
            () => {
                setSnackbarData?.({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_COPY_LINK_SUCCESS,
                    icon: LinkIcon,
                });
            },
            () => {
                setSnackbarData?.({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_COPY_LINK_ERROR,
                    icon: CautionIcon,
                    hasFailure: true,
                });
            },
        );
    };

    const stopInternalPostLinkCopyClick = (e: React.MouseEvent): void => {
        e.preventDefault();
        e.stopPropagation();
    };

    const onClick = async (e: React.MouseEvent): Promise<void> => {
        e.stopPropagation();
        if (
            internalPostLinkTarget
            && onNavigateInternalPostLink
        ) {
            logInternalPostLink('inlineLinkClick', {
                href,
                paragraphId: internalPostLinkTarget.paragraphId,
                chapterId: internalPostLinkTarget.chapterId || null,
                sectionId: internalPostLinkTarget.sectionId || null,
                hasResolvedPreviewSnippet: !!resolvedInternalPostLinkPreviewData?.snippet
                    && !resolvedInternalPostLinkPreviewData?.usesFallbackSnippet,
            });
            onNavigateInternalPostLink(internalPostLinkTarget);
        } else {
            window.open(href, '_blank');
        }

        if (
            readOnly
            && postId
            && currentSessionId
        ) {
            // Record user action
            const actionId = uuidv4();
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.clickInlineLink,
                actionId,
                sessionId: currentSessionId,
                payload: {
                    id,
                    href,
                    postId,
                    editorType,
                },
            });
            // Increment clicks
            updateMedia({
                test: isTestMode(),
                mediaId: id,
                type: MEDIA_TYPE.link,
                clicksToAdd: [actionId],
            });
        }
    };

    // ===== Side Effects =====

    useEffect(() => {
        setLinkMetadata(null);
        setHydratedInternalPostLinkPreviewData(null);
    }, [href]);
    useEffect(() => {
        if (
            !showCard
            || !internalPostLinkTarget
        ) {
            return;
        }

        logInternalPostLink('inlineLinkPreviewCardShown', {
            href,
            paragraphId: internalPostLinkTarget.paragraphId,
            chapterId: internalPostLinkTarget.chapterId || null,
            sectionId: internalPostLinkTarget.sectionId || null,
            initialSnippetLength: internalPostLinkPreviewData?.snippet?.length || 0,
            hydratedSnippetLength: hydratedInternalPostLinkPreviewData?.snippet?.length || 0,
        });
    }, [
        href,
        hydratedInternalPostLinkPreviewData?.snippet,
        internalPostLinkPreviewData?.snippet,
        internalPostLinkTarget,
        logInternalPostLink,
        showCard,
    ]);

    const updateLinkCardPlacement = useCallback((): void => {
        if (
            !readOnly
            || !showCard
            || !childRef.current
            || !parentRef
            || isMobile()
        ) {
            return;
        }

        const linkNode = childRef.current.parentElement;
        if (!linkNode) {
            return;
        }

        const measuredCardRect = linkCardRef.current?.getBoundingClientRect();
        const resolvedCardWidth = measuredCardRect?.width || (hasDetailedLinkCard
            ? LINK_CARD_WITH_METADATA_WIDTH
            : LINK_CARD_WITHOUT_METADATA_WIDTH);
        const resolvedCardHeight = measuredCardRect?.height || (hasDetailedLinkCard
            ? LINK_CARD_WITH_METADATA_HEIGHT
            : LINK_CARD_WITHOUT_METADATA_HEIGHT);
        const nextPlacement = resolveInlineLinkCardPlacement({
            linkRect: linkNode.getBoundingClientRect(),
            anchorPoint: linkCardAnchorPointRef.current,
            cardWidth: resolvedCardWidth,
            cardHeight: resolvedCardHeight,
            viewportWidth: window.innerWidth,
            viewportHeight: window.innerHeight,
            viewportPadding: 0.5 * BODY_FONT_SIZE,
            verticalOffset: 0.3125 * BODY_FONT_SIZE,
        });

        if (nextPlacement.cardAbove !== cardAbove) {
            setCardAbove(nextPlacement.cardAbove);
        }
        if (nextPlacement.top !== linkCardTop) {
            setLinkCardTop(nextPlacement.top);
        }
        if (nextPlacement.left !== linkCardLeft) {
            setLinkCardLeft(nextPlacement.left);
        }
    }, [
        cardAbove,
        hasDetailedLinkCard,
        linkCardLeft,
        linkCardTop,
        parentRef,
        readOnly,
        showCard,
    ]);

    // Determine position of link card
    useEffect(() => {
        if (
            !readOnly
            || !showCard
            || !childRef.current
            || !parentRef
            || isMobile()
        ) {
            return undefined;
        }

        const frameId = window.requestAnimationFrame(() => {
            updateLinkCardPlacement();
        });
        window.addEventListener('resize', updateLinkCardPlacement);
        window.addEventListener('scroll', updateLinkCardPlacement, true);

        return () => {
            window.cancelAnimationFrame(frameId);
            window.removeEventListener('resize', updateLinkCardPlacement);
            window.removeEventListener('scroll', updateLinkCardPlacement, true);
        };
    }, [
        resolvedInternalPostLinkPreviewData?.snippet,
        linkMetadata,
        parentRef,
        readOnly,
        showCard,
        updateLinkCardPlacement,
    ]);

    // if link url modified
    // update in toolbar
    useEffect(() => {
        // Get Link Metadata
        if (
            showCard
            && !linkMetadata
            && !internalPostLinkTarget
        ) {
            fetchURLMetadata(
                href,
                (result: HttpsCallableResult<unknown>) => {
                    const metadata: ILinkMetadata = result.data as ILinkMetadata;
                    setLinkMetadata(metadata);
                },
            );
        }
    }, [
        href,
        internalPostLinkTarget,
        linkMetadata,
        showCard,
    ]);
    useEffect(() => {
        if (
            !showCard
            || !readOnly
            || !internalPostLinkTarget
            || !postId
            || (
                !!resolvedInternalPostLinkPreviewData?.snippet
                && !resolvedInternalPostLinkPreviewData?.usesFallbackSnippet
            )
            || (
                !!hydratedInternalPostLinkPreviewData
                && hydratedInternalPostLinkPreviewData.usesFallbackSnippet
            )
        ) {
            return undefined;
        }

        logInternalPostLink('inlineLinkPreviewMissingSnippet', {
            href,
            paragraphId: internalPostLinkTarget.paragraphId,
            chapterId: internalPostLinkTarget.chapterId || null,
            sectionId: internalPostLinkTarget.sectionId || null,
            hasRootPostValueNodes: !!rootPostValueNodes?.length,
            hasRootPostPreviewValueNodes: !!rootPostPreviewValueNodes?.length,
            chapterCount: chapters?.length || 0,
        });

        const cachedInternalPostLinkPreviewData = internalPostLinkPreviewCacheKey
            ? hydratedInternalPostLinkPreviewDataCache.get(internalPostLinkPreviewCacheKey) || null
            : null;
        if (cachedInternalPostLinkPreviewData) {
            logInternalPostLink('inlineLinkPreviewHydrationCacheHit', {
                href,
                paragraphId: internalPostLinkTarget.paragraphId,
                hasSnippet: !!cachedInternalPostLinkPreviewData.snippet,
            });
            setHydratedInternalPostLinkPreviewData(cachedInternalPostLinkPreviewData);
            return undefined;
        }

        let cancelled = false;
        logInternalPostLink('inlineLinkPreviewHydrationRequested', {
            href,
            paragraphId: internalPostLinkTarget.paragraphId,
            chapterId: internalPostLinkTarget.chapterId || null,
            sectionId: internalPostLinkTarget.sectionId || null,
        });
        getPost({
            test: isTestMode(),
            postId,
            fullPost: false,
            chapterId: internalPostLinkTarget.chapterId,
            sectionId: internalPostLinkTarget.sectionId,
            includeReadOnlyValueNodes: true,
            responseTier: POST_RESPONSE_TIER.critical,
        }).then((postResult) => {
            if (cancelled) {
                return;
            }

            const hydratedPreviewData = resolveInternalPostLinkPreviewData({
                href,
                rootPostTitle,
                rootPostValueNodes: postResult.post?.valueNodes || rootPostValueNodes,
                rootPostPreviewValueNodes: postResult.post?.previewValueNodes || rootPostPreviewValueNodes,
                chapters: buildHydratedInternalPostLinkPreviewChapters({
                    chapters,
                    target: internalPostLinkTarget,
                    postResult,
                }),
            });
            if (!hydratedPreviewData) {
                return;
            }

            const hydratedSnippetLength = hydratedPreviewData.snippet?.length || 0;
            if (
                !hydratedSnippetLength
                || hydratedPreviewData.usesFallbackSnippet
            ) {
                logInternalPostLink('inlineLinkPreviewHydrationMissingSnippet', {
                    href,
                    paragraphId: internalPostLinkTarget.paragraphId,
                    chapterId: internalPostLinkTarget.chapterId || null,
                    sectionId: internalPostLinkTarget.sectionId || null,
                    hasPostValueNodes: !!postResult.post?.valueNodes?.length,
                    hasPostPreviewValueNodes: !!postResult.post?.previewValueNodes?.length,
                    hasChapterValueNodes: !!postResult.chapter?.valueNodes?.length,
                    hasChapterPreviewValueNodes: !!postResult.chapter?.previewValueNodes?.length,
                    hasSectionValueNodes: !!postResult.chapterSection?.valueNodes?.length,
                    hasSectionPreviewValueNodes: !!postResult.chapterSection?.previewValueNodes?.length,
                });
            }

            logInternalPostLink('inlineLinkPreviewHydrationResolved', {
                href,
                paragraphId: internalPostLinkTarget.paragraphId,
                snippetLength: hydratedSnippetLength,
            });
            if (internalPostLinkPreviewCacheKey) {
                hydratedInternalPostLinkPreviewDataCache.set(
                    internalPostLinkPreviewCacheKey,
                    hydratedPreviewData,
                );
            }
            setHydratedInternalPostLinkPreviewData(hydratedPreviewData);
        }).catch((error) => {
            logInternalPostLink('inlineLinkPreviewHydrationFailed', {
                href,
                paragraphId: internalPostLinkTarget.paragraphId,
                error: error instanceof Error ? error.message : 'unknown',
            });
        });

        return () => {
            cancelled = true;
        };
    }, [
        chapters,
        href,
        internalPostLinkPreviewCacheKey,
        internalPostLinkTarget,
        logInternalPostLink,
        postId,
        readOnly,
        resolvedInternalPostLinkPreviewData?.snippet,
        rootPostPreviewValueNodes,
        rootPostTitle,
        rootPostValueNodes,
        showCard,
    ]);

    const {
        start: delayHideLinkCard,
        clear: clearDelayHideLinkCard,
    } = useTimeout(() => {
        setShowCard(false);
    }, TRANSITION_LINK_CARD_DELAY);

    const showInlineLinkPreviewCard = useCallback((): void => {
        clearDelayHideLinkCard();
        setShowCard(true);
        dispatchInlineLinkPreviewCardOpened(inlineLinkPreviewCardIdRef.current);
    }, [clearDelayHideLinkCard]);

    const showInlineLinkPreviewCardFromLink = useCallback((e: React.MouseEvent | React.TouchEvent): void => {
        if ('clientX' in e && 'clientY' in e) {
            linkCardAnchorPointRef.current = {
                x: e.clientX,
                y: e.clientY,
            };
        }

        showInlineLinkPreviewCard();
    }, [showInlineLinkPreviewCard]);

    useEffect(() => {
        if (
            !readOnly
            || typeof window === 'undefined'
        ) {
            return undefined;
        }

        const onInlineLinkPreviewCardOpened = (event: Event): void => {
            const openedEvent = event as CustomEvent<IInlineLinkPreviewCardOpenedEventDetail>;
            if (openedEvent.detail?.previewId === inlineLinkPreviewCardIdRef.current) {
                return;
            }

            linkCardAnchorPointRef.current = undefined;
            clearDelayHideLinkCard();
            setShowCard(false);
        };

        window.addEventListener(INLINE_LINK_PREVIEW_CARD_OPENED_EVENT, onInlineLinkPreviewCardOpened);

        return () => {
            window.removeEventListener(INLINE_LINK_PREVIEW_CARD_OPENED_EVENT, onInlineLinkPreviewCardOpened);
        };
    }, [
        clearDelayHideLinkCard,
        readOnly,
    ]);

    // ===== Methods =====

    /**
     * Keeps the preview active while the pointer moves between the inline link,
     * the portaled card, and controls inside that card.
     */
    const isPointerStillInsideInlineLinkPreview = useCallback((
        target: EventTarget | null,
    ): boolean => {
        if (
            typeof Node === 'undefined'
            || !(target instanceof Node)
        ) {
            return false;
        }

        const nextHoverTarget = target;
        const linkNode = childRef.current?.parentElement;
        return !!(
            linkNode?.contains(nextHoverTarget)
            || linkCardRef.current?.contains(nextHoverTarget)
        );
    }, []);

    /**
     * Moving from a card control back onto the card does not fire another
     * mouseenter on the card, so the custom cursor needs to be reassigned here.
     */
    const restoreInlineLinkPreviewCardCursorTarget = useCallback((
        target: EventTarget | null,
    ): void => {
        if (
            typeof Node === 'undefined'
            || !(target instanceof Node)
            || !linkCardRef.current?.contains(target)
        ) {
            return;
        }

        onCursorEnter(
            CURSOR_TARGET.editorButton,
            [CURSOR_SIGN.click],
            linkCardRef.current,
        );
    }, [onCursorEnter]);

    const onMouseEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        if (!isMobile()) {
            showInlineLinkPreviewCard();
        }

        onCursorEnter(
            CURSOR_TARGET.editorButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onMouseLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        const nextHoverTarget = getInlineLinkPreviewMouseLeaveRelatedTarget(e);
        if (isPointerStillInsideInlineLinkPreview(nextHoverTarget)) {
            onCursorLeave(e);
            restoreInlineLinkPreviewCardCursorTarget(nextHoverTarget);
            return;
        }

        if (!isMobile()) {
            clearDelayHideLinkCard();
            delayHideLinkCard();
        }

        onCursorLeave(e);
    };

    const onInlineLinkMouseEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        if (!isMobile()) {
            showInlineLinkPreviewCardFromLink(e);
        }

        onCursorEnter(
            CURSOR_TARGET.linkCard,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const renderLinkCardContent = (): React.ReactElement => {
        if (resolvedInternalPostLinkPreviewData) {
            return (
                <InternalPostLinkCardContent>
                    <InternalPostLinkCopyButtonContainer
                        onClick={stopInternalPostLinkCopyClick}
                    >
                        <Button
                            type={BUTTON_TYPE.secret}
                            icon={LinkIcon}
                            iconFillColor={themeObj.verascopeColor.purple400}
                            background={themeObj.verascopeColor.purple400}
                            className={HOVER_TARGET_CLASSNAME}
                            customCursorActive={customCursorActive}
                            height={1.625 * BODY_FONT_SIZE}
                            width={1.625 * BODY_FONT_SIZE}
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                            onTouchStart={onCopyInternalPostLink}
                            onMouseDown={onCopyInternalPostLink}
                        />
                    </InternalPostLinkCopyButtonContainer>
                    <InternalPostLinkCardEyebrow>
                        INTERNAL LINK
                    </InternalPostLinkCardEyebrow>
                    {internalPreviewPrimaryText && (
                        <InternalPostLinkCardTitle>
                            {applyCharacterLimit(
                                internalPreviewPrimaryText,
                                44,
                            )}
                        </InternalPostLinkCardTitle>
                    )}
                    {internalPreviewSecondaryText && (
                        <InternalPostLinkCardScope
                            $isFallback={internalPreviewUsesFallbackSnippet}
                            $isAuthLockedFallback={internalPreviewUsesAuthLockedFallbackSnippet}
                        >
                            {applyCharacterLimit(
                                internalPreviewSecondaryText,
                                130,
                            )}
                        </InternalPostLinkCardScope>
                    )}
                </InternalPostLinkCardContent>
            );
        }

        if (linkMetadata) {
            return (
                <>
                    <LinkCardImageContainer>
                        <LinkCardImage
                            width={LINK_CARD_IMAGE_WIDTH}
                            height={LINK_CARD_IMAGE_HEIGHT}
                            {...(linkMetadata?.image
                                ? {
                                    url: linkMetadata.image,
                                } : {}
                            )}
                        >
                            {!linkMetadata.image
                            && (
                                <ReactSVG
                                    src={LinkIcon}
                                />
                            )}
                        </LinkCardImage>
                    </LinkCardImageContainer>
                    <LinkCardInfoContainer>
                        <LinkCardTitle
                            className={HOVER_TARGET_CLASSNAME}
                            customCursorActive={customCursorActive}
                            href={href}
                            target="_blank"
                            color={color}
                            fontMultiplier={LINK_CARD_FONT_MULTIPLIER}
                            {...getTouchMouseEventHandlers({
                                onMouseEnter,
                                onMouseLeave,
                            })}
                            {...getPressEventHandlers({
                                onTouchStart: (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                },
                                onMouseDown: (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                },
                            })}
                        >
                            {linkMetadata.title
                                ? applyCharacterLimit(
                                    linkMetadata.title,
                                    LINK_CARD_MAX_TITLE_COUNT,
                                )
                                : 'Untitled Page'}
                        </LinkCardTitle>
                        {secondaryText
                        && (
                            <LinkCardAddress
                                fontMultiplier={LINK_CARD_FONT_MULTIPLIER}
                            >
                                {secondaryText}
                            </LinkCardAddress>
                        )}
                    </LinkCardInfoContainer>
                </>
            );
        }

        return (
            <TemporaryLinkText
                href={href}
                target="_blank"
                color={color}
                customCursorActive={customCursorActive}
                fontMultiplier={LINK_CARD_FONT_MULTIPLIER}
            >
                {href
                && applyCharacterLimit(
                    href,
                    28,
                )}
            </TemporaryLinkText>
        );
    };

    return (
        <Container
            color={color}
            readOnly={readOnly}
            hasSuccess={hasSuccess}
            hasError={hasError}
            {...(readOnly
                ? {
                    className: HOVER_TARGET_CLASSNAME,
                    onMouseEnter: onInlineLinkMouseEnter,
                    onMouseLeave,
                    onClick,
                } : {}
            )}
            {...attributes}
            // Necessary to avoid: Cannot resolve a Slate point from DOM point
            // Reference: https://github.com/ianstormtaylor/slate/issues/3930
            contentEditable={!readOnly}
            suppressContentEditableWarning
        >
            {children}
            {readOnly && internalPostLinkNavigationActive && (
                <InlineStatusPill>
                    <InlineStatusSpinner
                        src={SpinnerIcon}
                        alt=""
                    />
                    <InlineStatusText>
                        Jumping to section
                    </InlineStatusText>
                </InlineStatusPill>
            )}
            <RefNode
                ref={childRef}
            />
            {readOnly && createPortal(
                <Transition
                    in={showCard}
                    timeout={{
                        enter: LINK_CARD_TRANSITION_DURATION,
                        exit: LINK_CARD_TRANSITION_DURATION,
                    }}
                >
                    {(state) => (
                        <LinkCard
                            ref={linkCardRef}
                            className={HOVER_TARGET_CLASSNAME}
                            visible={showCard}
                            customCursorActive={customCursorActive}
                            hasDetailedLinkCard={hasDetailedLinkCard}
                            top={linkCardTop}
                            left={linkCardLeft}
                            style={{
                                ...FADE_IN_DEFAULT_STYLE({
                                    direction: cardAbove ? 'up' : 'down',
                                    offset: 20,
                                    duration: LINK_CARD_TRANSITION_DURATION,
                                    easing: themeObj.motion.standardEasing,
                                    horizontalCenter: false,
                                }),
                                ...FADE_IN_TRANSITION_STYLES({
                                    direction: cardAbove ? 'up' : 'down',
                                    offset: 20,
                                    horizontalCenter: false,
                                })[state],
                            }}
                        >
                            <LinkCardContainer
                                className={HOVER_TARGET_CLASSNAME}
                                readOnly
                                hasLinkMetadata={hasDetailedLinkCard}
                                toolbarType={EDITOR_TOOLBAR_TYPE.selection}
                                {...getTouchMouseEventHandlers({
                                    onMouseEnter,
                                    onMouseLeave,
                                })}
                            >
                                <LinkCardTopContent
                                    hasLinkMetadata={hasDetailedLinkCard}
                                >
                                    {renderLinkCardContent()}
                                </LinkCardTopContent>
                            </LinkCardContainer>
                        </LinkCard>
                    )}
                </Transition>,
                document.body,
            )}
        </Container>
    );
}

// ===== Styled Components =====

interface ContainerProps {
    customCursorActive: boolean,
    readOnly: boolean,
    hasSuccess: boolean,
    hasError: boolean,
}
const Container = styled.span<ContainerProps>`
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
    position: relative;
    font-weight: 500;
    color: ${({ color, theme }) => (color
        ? setColorLightness(
            color,
            45,
        )
        : theme.color.neutral800
    )};
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'pointer')};
    text-decoration: underline;
    transition: color 0.3s;

    & ${SlateLeaf} {
        font-size: inherit;
        line-height: inherit;
    }

    background: ${({
        hasSuccess,
        hasError,
        theme,
    }) => {
        if (hasError) {
            return setColorLightness(
                theme.color.error,
                ERROR_BACKGROUND_LIGHTNESS_VALUE,
            );
        }

        if (hasSuccess) {
            return setColorLightness(
                theme.verascopeColor.blue200,
                SUCCESS_BACKGROUND_LIGHTNESS_VALUE,
            );
        }

        // Rather than be none, we want a transparent the background color
        // so that the background transitions in response to hover events
        return 'transparent';
    }};

    ${({ customCursorActive }) => customCursorActive && `
        &,
        & * {
            cursor: none !important;
        }
    `}

    ${({ readOnly, color }) => readOnly && color && `
        &:hover {
            color: ${setColorLightness(color, 30)};
        }
    `};
`;

interface LinkCardProps {
    visible: boolean,
    customCursorActive: boolean,
    hasDetailedLinkCard: boolean,
    top: number,
    left: number,
}
const LinkCard = styled.div<LinkCardProps>`
    position: fixed;
    top: ${({ top }) => `${top}px`};
    left: ${({ left }) => `${left}px`};
    width: ${({ hasDetailedLinkCard }) => `${hasDetailedLinkCard
        ? LINK_CARD_WITH_METADATA_WIDTH
        : LINK_CARD_WITHOUT_METADATA_WIDTH
    }px`};
    min-height: ${({ hasDetailedLinkCard }) => `${hasDetailedLinkCard
        ? LINK_CARD_WITH_METADATA_HEIGHT
        : LINK_CARD_WITHOUT_METADATA_HEIGHT
    }px`};
    pointer-events: ${({ visible }) => (visible
        ? 'auto'
        : 'none'
    )};
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'pointer')};
    z-index: 1;
`;

const RefNode = styled.span``;

const InternalPostLinkCardContent = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    min-height: 100%;
    padding: ${`${0.875 * BODY_FONT_SIZE}px ${0.9375 * BODY_FONT_SIZE}px`};
`;

const InternalPostLinkCopyButtonContainer = styled.div`
    position: absolute;
    top: ${`${0.5625 * BODY_FONT_SIZE}px`};
    right: ${`${0.5625 * BODY_FONT_SIZE}px`};
`;

const InternalPostLinkCardEyebrow = styled.p`
    margin: 0;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: ${`${0.6875 * BODY_FONT_SIZE}px`};
    font-weight: 700;
    line-height: 1.2;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.color.neutral600};
`;

const InternalPostLinkCardTitle = styled.p`
    margin: ${`${0.375 * BODY_FONT_SIZE}px 0 0`};
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: ${`${0.9375 * BODY_FONT_SIZE}px`};
    font-weight: 700;
    line-height: 1.35;
    color: ${({ theme }) => theme.color.neutral1000};
`;

interface InternalPostLinkCardScopeProps {
    $isFallback?: boolean,
    $isAuthLockedFallback?: boolean,
}

const InternalPostLinkCardScope = styled.p<InternalPostLinkCardScopeProps>`
    margin: ${`${0.25 * BODY_FONT_SIZE}px 0 0`};
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: ${`${0.8125 * BODY_FONT_SIZE}px`};
    font-weight: 600;
    line-height: 1.45;
    color: ${({ theme, $isFallback, $isAuthLockedFallback }) => {
        if ($isAuthLockedFallback) {
            return theme.verascopeColor.purple500;
        }

        if ($isFallback) {
            return theme.verascopeColor.red200;
        }

        return theme.color.neutral700;
    }};
`;

const InlineStatusPill = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.45em;
    vertical-align: middle;
    padding: 0.28em 0.7em 0.28em 0.45em;
    border-radius: 999px;
    background: ${({ theme }) => theme.verascopeColor.purple200};
    color: ${({ theme }) => theme.color.white};
    box-shadow: ${({ theme }) => theme.color.boxShadow100};
`;

const InlineStatusSpinner = styled.img`
    width: 0.95em;
    height: 0.95em;
    animation: rotate 2s linear infinite;
    flex: 0 0 auto;
`;

const InlineStatusText = styled.span`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: ${`${0.8 * BODY_FONT_SIZE}px`};
    font-weight: 500;
    line-height: 1;
    white-space: nowrap;
`;

export default InlineLink;
